/* eslint-disable @typescript-eslint/no-unused-vars */

import { GetRowIdParams, GridReadyEvent, RowSelectedEvent } from '@ag-grid-community/core';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { AgFns, GridOptionsExt, ISortModel, ProxAgFns, StatusService, wrapBusy } from '@core';
import {
  ActiveStatusEnum,
  ApprovalTreeUserGroup,
  ProgramTypeEnum,
  ProgramUserGroup,
  ProgramUserGroupExclusion,
  ProgramUserGroupMap,
} from '@models';
import { ProgramFrmComponent } from './program-frm.component';
import * as _ from 'lodash';
import { EntityFns } from '@data';
import { DateFns } from '@utils';

@Component({
  selector: 'prox-program-user-groups-sub',
  templateUrl: './program-user-groups.sub.component.html',
  standalone: false,
})
export class ProgramUserGroupsSubComponent implements OnInit, OnChanges {
  @Input() visible!: boolean;
  @Input() parent!: ProgramFrmComponent;

  atugGridOptions: GridOptionsExt = {};
  allAtugs: ApprovalTreeUserGroup[] = [];
  atugs: ApprovalTreeUserGroup[] = [];
  showInactivePugs = false;
  selectedAtug?: ApprovalTreeUserGroup;
  selectedPug?: ProgramUserGroup;

  pugmGridOptions: GridOptionsExt = {};
  pugms: ProgramUserGroupMap[] = [];

  accountUserIdCountMap!: object;

  constructor(private statusService: StatusService) {
    //
  }

  get program() {
    return this.parent.program;
  }

  getApprovalTreeName() {
    return this.parent.program.approvalTree?.name ?? 'n/a';
  }

  ngOnInit() {
    this.atugGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onAtugGridReady,
      onRowSelected: this.onAtugRowSelected,
      getRowId: (rowIdParams: GetRowIdParams) => {
        const ug = rowIdParams.data as ApprovalTreeUserGroup;
        return ug.programUserGroupId;
      },
      rowModelType: 'clientSide',
    });

    this.pugmGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onPugmGridReady,
      getRowId: (rowIdParams: GetRowIdParams) => {
        const pugm = rowIdParams.data as ProgramUserGroupMap;
        return pugm.accountUserId.toString();
      },
      rowModelType: 'clientSide',
      enableCellTextSelection: true,
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.refresh();
  }

  @wrapBusy
  async refresh(forceRefresh = true) {
    if (!this.visible && !forceRefresh) return;
    this.parent.updateComponentNameMap(this);

    this.allAtugs = this.program.approvalTree?.approvalTreeUserGroups ?? [];
    this.atugs = this.applyShowInactiveFilter(this.allAtugs);

    // Users associated with user groups are no longer queried until UserGroups sub is opened

    if (_.isEmpty(this.atugGridOptions)) return;
    this.onAtugGridReady();
    this.onPugmGridReady();
    AgFns.refreshGrid(this.atugGridOptions, this.atugs);
    AgFns.refreshGrid(this.pugmGridOptions, this.pugms);
  }

  // Show-Inactive semantics: hide only Inactive PUGs when unchecked; keep Hold + Phaseout.
  // See show-inactive-checkbox-semantics memory.
  private applyShowInactiveFilter(atugs: ApprovalTreeUserGroup[]): ApprovalTreeUserGroup[] {
    if (this.showInactivePugs) return atugs;
    return atugs.filter((a) => a.programUserGroup?.activeStatusId !== ActiveStatusEnum.Inactive);
  }

  onShowInactivePugsChanged() {
    this.atugs = this.applyShowInactiveFilter(this.allAtugs);
    AgFns.refreshGrid(this.atugGridOptions, this.atugs);
  }

  onAtugGridReady(event?: GridReadyEvent) {
    const colDefs = [
      {
        ...AgFns.createButtonProps('View/Edit', this.parent.onEditAdminUserGroup.bind(this.parent), { label: 'Edit' }),
        ...AgFns.fixWidth(90),
      },
      {
        headerName: 'Group Name',
        field: 'programUserGroup.name',
        filter: 'agTextColumnFilter',
        cellRenderer: 'agGroupCellRenderer',
        ...AgFns.flexWidth(200),
      },
      {
        headerName: 'Purchase Order',
        field: 'accountPurchaseOrder.purchaseOrder',
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(140),
      },
      {
        headerName: 'Procurement Card',
        field: 'accountProcurementCard.namedesc',
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(140),
      },
      {
        ...ProxAgFns.getActiveStatusColDef('activeStatus.name'),
        headerName: 'Org User Group Status',
        ...AgFns.fixWidth(160),
        headerTooltip: "Status of this user group within this Program's organization tree. Changed from the Proximity Organization page.",
      },
      {
        ...ProxAgFns.getActiveStatusColDef('programUserGroup.activeStatus.name'),
        headerName: 'Program User Group Status',
        ...AgFns.fixWidth(180),
        headerTooltip:
          'Status of the underlying Program User Group itself. If Hold, the group is inactive for ALL programs that reference it, not just this one. Changed from the Program User Groups page.',
      },
      ...(this.program.programTypeId !== ProgramTypeEnum.Inventory
        ? [
            {
              headerName: 'Restricted Ordering Time Frame',
              children: [
                {
                  headerName: 'Start Date ',
                  valueFormatter: AgFns.dateFormatter,
                  valueGetter: (p) => this.getOrderTimeFrameStartDate(p),
                  ...AgFns.fixWidth(140),
                },
                {
                  headerName: 'Duration (Days)',
                  field: 'orderTimeFrameDurationDays',
                  ...AgFns.fixWidth(140),
                },
                {
                  headerName: 'Ship Date',
                  valueFormatter: AgFns.dateFormatter,
                  valueGetter: (p) => this.getOrderTimeFrameShipDate(p),
                  ...AgFns.fixWidth(140),
                },
              ],
            },
          ]
        : []),
      //ProxAgFns.getWorkingStatusDef('Status', this.statusService),
    ];
    const sortModel: ISortModel = [{ colId: 'name', sort: 'asc' }];

    AgFns.initGrid(event ?? this.atugGridOptions, colDefs, sortModel);
    AgFns.sizeColumnsToFit(this.atugGridOptions);
    AgFns.selectDefaultRow(this.atugGridOptions);
  }

  getOrderTimeFrameStartDate(p: any) {
    if (p.data.orderTimeFrameStartDays === 0) return <Date>this.parent.program.startDate;
    if (!p.data.orderTimeFrameStartDays) return null;
    return DateFns.dateAdd(<Date>this.parent.program.startDate, p.data.orderTimeFrameStartDays, 'd');
  }

  getOrderTimeFrameShipDate(p: any) {
    if (!p.data.orderTimeFrameShipByFromStartDays) return null;
    return DateFns.dateAdd(<Date>this.getOrderTimeFrameStartDate(p), p.data.orderTimeFrameShipByFromStartDays, 'd');
  }

  onPugmGridReady(event?: GridReadyEvent) {
    const colDefs = [
      {
        ...AgFns.createButtonProps('Exclude/Include?', this.excludeIncludeFromGroup.bind(this), {
          calcLabel: this.calcExcludeIncludeLabel.bind(this),
          calcDisabled: () => this.parent.isReadOnly(),
        }),
        ...AgFns.fixWidth(250),
      },
      {
        headerName: '# Grps',
        valueGetter: (params) => {
          const pugm = params.data as ProgramUserGroupMap;
          return this.accountUserIdCountMap[pugm.accountUserId];
        },
        ...AgFns.fixWidth(110),
      },
      {
        headerName: 'Salutation',
        field: 'accountUser.proximityUser.salutation',
        filter: 'agSetColumnFilter',
        ...AgFns.fixWidth(120),
      },
      {
        headerName: 'First Name',
        field: 'accountUser.proximityUser.firstName',
        filter: 'agSetColumnFilter',
        ...AgFns.flexWidth(100),
      },
      {
        headerName: 'Last Name',
        field: 'accountUser.proximityUser.lastName',
        filter: 'agSetColumnFilter',
        ...AgFns.flexWidth(100),
      },
      // {
      //   headerName: 'Middle Name',
      //   field: 'accountUser.proximityUser.middleName',
      //   filter: 'agSetColumnFilter',
      //   ...AgFns.flexWidth(90),
      // },

      {
        headerName: 'Hire Date',
        field: 'accountUser.proximityUser.hireDate',
        editable: !this.parent.isReadOnly(),
        filter: 'agDateColumnFilter',
        cellEditor: 'agDateCellEditor',
        ...AgFns.fixWidth(140),
      },
      {
        headerName: 'Join Date',
        field: 'joinDate',
        editable: !this.parent.isReadOnly(),

        filter: 'agDateColumnFilter',
        cellEditor: 'agDateCellEditor',
        ...AgFns.fixWidth(140),
      },
      {
        headerName: 'E-Mail',
        field: 'accountUser.proximityUser.email',
        filter: 'agSetColumnFilter',
        ...AgFns.flexWidth(150),
      },
      {
        headerName: 'Gender',
        field: 'accountUser.gender.name',
        filter: 'agSetColumnFilter',
        ...AgFns.fixWidth(120),
      },
      {
        headerName: 'Status',
        field: 'accountUser.proximityUser.activeStatus.name',
        filter: 'agSetColumnFilter',
        cellStyle: (params): any => {
          const pugm = params.data as ProgramUserGroupMap;
          const statusId = pugm?.accountUser?.proximityUser?.activeStatusId;
          return statusId !== ActiveStatusEnum.Active ? { color: 'red', fontWeight: '600' } : null;
        },
        ...AgFns.fixWidth(120),
      },
      // ProxAgFns.getWorkingStatusDef('Status', this.statusService)
    ];
    const sortModel: ISortModel = [{ colId: 'accountUser.proximityUser.lastName', sort: 'asc' }];
    this.pugmGridOptions.rowClassRules = {
      'row-excluded': (params) => {
        const pugm = params.data as ProgramUserGroupMap;
        const accountUser = pugm.accountUser;
        const exclusion = this.selectedPug?.programUserGroupExclusions.find(
          (x) => x.accountUserId == accountUser.id && x.programId == this.program.id,
        );
        return exclusion != null;
      },
    };

    AgFns.initGrid(event ?? this.pugmGridOptions, colDefs, sortModel);
  }

  async onAtugRowSelected(event: RowSelectedEvent) {
    if (!event.node.isSelected()) return;
    const atug = event.data as ApprovalTreeUserGroup;

    if (atug == null) return;
    // 'programUserGroupMaps.accountUser.proximityUser',
    const pugms = await this.parent.dbQueryService.getProgramUserGroupMaps(atug.programUserGroup.id);
    this.accountUserIdCountMap = _.countBy(pugms, (x) => x.accountUserId);

    atug.programUserGroup.programUserGroupExclusions
      .filter((y) => y.programId == this.program.id)
      .forEach((y) => (this.accountUserIdCountMap[y.accountUserId] -= 1));

    this.selectedAtug = atug;
    this.selectedPug = atug.programUserGroup;
    this.pugms = atug.programUserGroup.programUserGroupMaps;
    AgFns.refreshGrid(this.pugmGridOptions, this.pugms);
  }

  async excludeIncludeFromGroup(pugm: ProgramUserGroupMap) {
    const accountUser = pugm.accountUser;
    if (this.selectedPug == null) return;
    let exclusion = this.selectedPug?.programUserGroupExclusions.find(
      (x) => x.accountUserId == accountUser.id && x.programId == this.program.id,
    );
    if (exclusion == null) {
      const item = this.parent.dbSaveService.uow.undoIfDeleted(ProgramUserGroupExclusion, [
        this.program.id,
        this.selectedPug.id,
        accountUser.id,
      ]);
      if (item == null) {
        exclusion = this.parent.dbSaveService.createEntity(ProgramUserGroupExclusion, {
          programId: this.program.id,
          programUserGroupId: this.selectedPug.id,
          accountUserId: accountUser.id,
        });
      }
      this.accountUserIdCountMap[accountUser.id] = this.accountUserIdCountMap[accountUser.id] - 1;
    } else {
      EntityFns.deleteOrDetach(exclusion.entityAspect);
      this.accountUserIdCountMap[accountUser.id] = this.accountUserIdCountMap[accountUser.id] + 1;
    }
    // This is a hack to allow exclusion/inclusions to save during activation - otherwise the dup exclusion
    // logic will fail because it only looks at saved records.
    if (this.parent.activatingStatusCode != null && exclusion != null) {
      await this.parent.dbSaveService.saveSelectedChanges([exclusion]);
    }
    AgFns.refreshGrid(this.pugmGridOptions, this.pugms);
  }

  calcExcludeIncludeLabel(pugm: ProgramUserGroupMap) {
    const accountUser = pugm.accountUser;
    const exclusion = this.selectedPug?.programUserGroupExclusions.find(
      (x) => x.accountUserId == accountUser.id && x.programId == this.program.id,
    );
    return exclusion != null ? 'Currently Excluded (Click to Include)' : 'Currently Included (Click to Exclude)';
  }

  orgName() {
    if (this.parent.program && this.program.approvalTree) {
      return this.parent.program.approvalTree.name;
    } else {
      return '* New Program *';
    }
  }
}
